import { useState } from "react";
import { Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreatePrescriptionTemplate } from "@/lib/mutations";
import { usePrescriptionTemplateGroups } from "@/lib/queries";
import type { MedicineRow } from "@/lib/queries";
import type { TemplateMedicine } from "@emr/shared";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  medicines: MedicineRow[];
}

/**
 * "Save as template" — snapshots the current live prescription rows as a
 * named prescription template. Opened from the prescription card header
 * (next to the Templates button). Row metadata (id/prescriptionId/
 * serialNo) is stripped; the value columns are frozen as-is.
 */
export function SaveAsPrescriptionTemplateDialog({ open, onOpenChange, medicines }: Props) {
  const [name, setName] = useState("");
  const NONE = "__none__";
  const [groupId, setGroupId] = useState<string>(NONE);
  const create = useCreatePrescriptionTemplate();
  const { data: groupsData } = usePrescriptionTemplateGroups();
  const groups = groupsData?.rows ?? [];

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const rows: TemplateMedicine[] = medicines.map((m) => ({
      drugId: m.drugId,
      name: m.name,
      composition: m.composition,
      formulation: m.formulation as TemplateMedicine["formulation"],
      strength: m.strength,
      frequency: m.frequency,
      taper: m.taper,
      duration: m.duration,
      instructions: m.instructions,
    }));
    try {
      await create.mutateAsync({ name: trimmed, medicines: rows, ...(groupId !== NONE ? { groupId } : {}) });
      onOpenChange(false);
      setName("");
      setGroupId(NONE);
    } catch { /* onError toast already fired (e.g. duplicate name 409) */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save prescription as template</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label>Template name *</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              placeholder="e.g. Migraine prophylaxis"
            />
          </div>
          <div>
            <Label>Group</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="No group (Ungrouped)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No group (Ungrouped)</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted">
            Saves the current {medicines.length} medicine{medicines.length === 1 ? "" : "s"} as a reusable template. The visit's prescription is not modified.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Save size={14} className="mr-1" /> Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
